import DragModal from "../../common/components/DragModal";
import {
  DialogContent,
  Divider,
  Typography,
  TableContainer,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Paper,
  Button,
  Box,
  TextField,
  TablePagination,
  Autocomplete,
  IconButton,
  Tooltip,
  Checkbox,
  Stack,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as DuplicateIcon,
  History as HistoryIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { TagListContext } from "../../common/contexts/TagListContext";
import { TenantContext } from "../../common/contexts/TenantContext";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  deleteTagListAndTags,
  duplicateTagListWithTags,
  getTagListById,
  removeTagLayerFromTagList,
  updateTagListMetadata,
} from "../../api/APIHelpers";
import { TagListHistoryModal } from "./TagListHistoryModal";
import { sortBy } from "lodash";
import { extractApiErrorMessage } from "../../common/helpers/ErrorUtils";

export const ManageTagListsModal = ({
  open,
  onClose,
  setInfoMessages,
  onLoadTagLists,
}) => {
  const availableTagLists = useContext(TagListContext);
  const tenantInfo = useContext(TenantContext);
  const [panel, setPanel] = useState("manage");

  const [tagLists, setTagLists] = useState([]);
  const [selectedTagListIds, setSelectedTagListIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [hardwareCategoryFilter, setHardwareCategoryFilter] = useState("");
  const [orderBy, setOrderBy] = useState("Name");
  const [orderDirection, setOrderDirection] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [selectedRemoveTagListId, setSelectedRemoveTagListId] = useState(-1);
  const [removableTags, setRemovableTags] = useState([]);
  const [selectedTagToRemove, setSelectedTagToRemove] = useState(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [activeTagList, setActiveTagList] = useState(null);
  const [editForm, setEditForm] = useState({
    Name: "",
    Description: "",
    HardwareCategory: "",
  });
  const [duplicateName, setDuplicateName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [historyTagList, setHistoryTagList] = useState(null);

  useEffect(() => {
    setTagLists(availableTagLists.TagLists || []);
  }, [availableTagLists]);

  useEffect(() => {
    if (open) {
      setPanel("manage");
      setSelectedTagListIds(new Set());
      setSearchQuery("");
      setHardwareCategoryFilter("");
      setOrderBy("Name");
      setOrderDirection("asc");
      setPage(0);
      setSelectedRemoveTagListId(-1);
      setRemovableTags([]);
      setSelectedTagToRemove(null);
    }
  }, [open]);

  useEffect(() => {
    const retrieveTags = async () => {
      if (selectedRemoveTagListId === -1) {
        setRemovableTags([]);
        setSelectedTagToRemove(null);
        return;
      }

      try {
        const tagList = await getTagListById(selectedRemoveTagListId);
        setRemovableTags(tagList.data.Tags || []);
      } catch (error) {
        setInfoMessages({
          type: "snackbar",
          message: `Failed to load tags for selected tag list: ${extractApiErrorMessage(error)}`,
          severity: "error",
          open: true,
        });
      }
    };

    retrieveTags();
  }, [selectedRemoveTagListId, setInfoMessages]);

  const refreshTagLists = async () => {
    if (availableTagLists.refreshTagListContext) {
      await availableTagLists.refreshTagListContext();
    }
  };

  const getTagListNameWithTenant = (tagList) => {
    if (!tenantInfo || !tenantInfo.availableTenants) {
      return (
        tagList?.Name || `Unnamed TagList - ID ${tagList?.TCTagListId ?? ""}`
      );
    }
    let tagListTenant = tenantInfo.availableTenants.find(
      (t) => t.TenantId === tagList.TenantId
    );
    let tagListTenantName = "";
    if (tagListTenant) {
      tagListTenantName = `(${tagListTenant.DisplayName})`;
    }
    let tagListName =
      tagList.Name || `Unnamed TagList - ID ${tagList.TCTagListId}`;
    return `${tagListName} ${tagListTenantName} ${tagList.IsTagStandard ? " (Tag Standard)" : ""}`;
  };

  const hardwareCategoryOptions = useMemo(() => {
    const categories = tagLists
      .map((tagList) => tagList.HardwareCategory)
      .filter(
        (category) => typeof category === "string" && category.trim().length > 0
      );
    return [...new Set(categories)].sort((a, b) => a.localeCompare(b));
  }, [tagLists]);

  const filteredTagLists = useMemo(() => {
    const filtered = tagLists.filter((tagList) => {
      const name = (tagList.Name || "").toLowerCase();
      const description = (tagList.Description || "").toLowerCase();
      const hardwareCategory = (tagList.HardwareCategory || "").toLowerCase();
      const query = searchQuery.toLowerCase();
      const matchesSearch = name.includes(query) || description.includes(query);
      const matchesHardwareCategory = hardwareCategoryFilter
        ? hardwareCategory === hardwareCategoryFilter.toLowerCase()
        : true;
      return matchesSearch && matchesHardwareCategory;
    });

    const sorted = [...filtered].sort((a, b) => {
      const directionMultiplier = orderDirection === "asc" ? 1 : -1;
      const valueA = `${a[orderBy] || ""}`.toLowerCase();
      const valueB = `${b[orderBy] || ""}`.toLowerCase();
      if (valueA < valueB) {
        return -1 * directionMultiplier;
      }
      if (valueA > valueB) {
        return 1 * directionMultiplier;
      }
      return 0;
    });

    return sorted;
  }, [hardwareCategoryFilter, orderBy, orderDirection, searchQuery, tagLists]);

  const paginatedTagLists = useMemo(
    () =>
      filteredTagLists.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [filteredTagLists, page, rowsPerPage]
  );

  const removeTagListOptions = useMemo(
    () =>
      availableTagLists.TagLists
        ? sortBy(availableTagLists.TagLists, (tagList) => tagList.Name)
        : [],
    [availableTagLists.TagLists]
  );

  const toggleTagListSelection = (tagListId) => {
    setSelectedTagListIds((previous) => {
      const next = new Set(previous);
      if (next.has(tagListId)) {
        next.delete(tagListId);
      } else {
        next.add(tagListId);
      }
      return next;
    });
  };

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDirection((previous) => (previous === "asc" ? "desc" : "asc"));
      return;
    }
    setOrderBy(column);
    setOrderDirection("asc");
  };

  const handleOpenEditDialog = (tagList) => {
    setActiveTagList(tagList);
    setEditForm({
      Name: tagList.Name || "",
      Description: tagList.Description || "",
      HardwareCategory: tagList.HardwareCategory || "",
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!activeTagList) {
      return;
    }
    setIsSubmitting(true);
    setInfoMessages({
      type: "snackbar",
      severity: "info",
      message: "Tag List update has been queued.",
      open: true,
    });
    try {
      await updateTagListMetadata(activeTagList.TCTagListId, {
        Name: editForm.Name,
        Description: editForm.Description,
        HardwareCategory: editForm.HardwareCategory,
        TenantId: activeTagList.TenantId,
        IsTagStandard: activeTagList.IsTagStandard,
        InsertedBy: activeTagList.InsertedBy,
        InsertedDate: activeTagList.InsertedDate,
      });
      await refreshTagLists();
      setEditDialogOpen(false);
      setInfoMessages({
        type: "snackbar",
        message: "Tag List updated successfully.",
        severity: "success",
        open: true,
      });
    } catch (error) {
      setInfoMessages({
        type: "snackbar",
        message: `There was an issue updating the tag list: ${extractApiErrorMessage(error)}`,
        severity: "error",
        open: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDuplicateDialog = (tagList) => {
    setActiveTagList(tagList);
    setDuplicateName(`${tagList.Name || ""} Copy`.trim());
    setDuplicateDialogOpen(true);
  };

  const handleDuplicate = async () => {
    if (!activeTagList || !duplicateName.trim()) {
      return;
    }

    setIsSubmitting(true);
    setInfoMessages({
      type: "snackbar",
      severity: "info",
      message: "Tag List duplicate has been queued.",
      open: true,
    });
    try {
      await duplicateTagListWithTags(
        activeTagList.TCTagListId,
        duplicateName.trim()
      );
      await refreshTagLists();
      setDuplicateDialogOpen(false);
      setInfoMessages({
        type: "snackbar",
        message: "Tag List duplicated successfully.",
        severity: "success",
        open: true,
      });
    } catch (error) {
      setInfoMessages({
        type: "snackbar",
        message: `There was an issue duplicating the tag list: ${extractApiErrorMessage(error)}`,
        severity: "error",
        open: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!activeTagList) {
      return;
    }
    setIsSubmitting(true);
    setInfoMessages({
      type: "snackbar",
      severity: "info",
      message: "Tag List delete has been queued.",
      open: true,
    });
    try {
      await deleteTagListAndTags(activeTagList.TCTagListId);
      await refreshTagLists();
      setSelectedTagListIds((previous) => {
        const next = new Set(previous);
        next.delete(activeTagList.TCTagListId);
        return next;
      });
      setDeleteDialogOpen(false);
      setInfoMessages({
        message: "Tag List deleted successfully.",
        severity: "success",
        type: "snackbar",
        open: true,
      });
    } catch (error) {
      setInfoMessages({
        type: "snackbar",
        message: `There was an issue deleting the tag list: ${extractApiErrorMessage(error)}`,
        severity: "error",
        open: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoadIntoGrid = async () => {
    const selectedTagLists = tagLists.filter((tagList) =>
      selectedTagListIds.has(tagList.TCTagListId)
    );
    if (selectedTagLists.length === 0) {
      setInfoMessages({
        type: "snackbar",
        message: "Select at least one tag list to load.",
        severity: "warning",
        open: true,
      });
      return;
    }

    try {
      await onLoadTagLists(selectedTagLists);
      onClose();
    } catch {
      // errors are surfaced by parent loader
    }
  };

  const handleRemoveTagFromList = async () => {
    if (!selectedTagToRemove || selectedRemoveTagListId === -1) {
      return;
    }
    setInfoMessages({
      type: "snackbar",
      severity: "info",
      message: "Tag remove has been queued.",
      open: true,
    });
    try {
      await removeTagLayerFromTagList(
        selectedRemoveTagListId,
        selectedTagToRemove.TCTagLayerId
      );
      const updatedTags = removableTags.filter(
        (tag) => tag.TCTagLayerId !== selectedTagToRemove.TCTagLayerId
      );
      setRemovableTags(updatedTags);
      setSelectedTagToRemove(null);
      await refreshTagLists();
      setInfoMessages({
        message: "Tag removed successfully.",
        severity: "success",
        type: "snackbar",
        open: true,
      });
    } catch (error) {
      setInfoMessages({
        type: "snackbar",
        message: `There was an issue removing the tag: ${extractApiErrorMessage(error)}`,
        severity: "error",
        open: true,
      });
    }
  };

  return (
    <>
      <DragModal
        open={open}
        title='manageTagLists'
        onClose={onClose}
        fullWidth
        maxWidth='lg'
        titlecomponent={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}>
            <Typography variant='h5' color='primary'>
              Manage Tag Lists
            </Typography>
            <IconButton onClick={onClose} size='small' aria-label='close'>
              <CloseIcon />
            </IconButton>
          </Box>
        }>
        <Divider />
        <DialogContent>
          <Stack direction='row' spacing={2} sx={{ mb: 2 }}>
            <Button
              variant={panel === "manage" ? "contained" : "outlined"}
              onClick={() => setPanel("manage")}>
              Manage Tag List
            </Button>
            <Button
              variant={panel === "remove" ? "contained" : "outlined"}
              onClick={() => setPanel("remove")}>
              Remove Tag from List
            </Button>
          </Stack>

          {panel === "manage" ? (
            <>
              <TextField
                fullWidth
                label='Search Tag List Name or Description'
                variant='outlined'
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                sx={{ mb: 2 }}
              />
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                sx={{ mb: 2 }}>
                <Autocomplete
                  fullWidth
                  options={hardwareCategoryOptions}
                  value={hardwareCategoryFilter || null}
                  onChange={(_, newValue) => {
                    setHardwareCategoryFilter(newValue || "");
                    setPage(0);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label='Filter Hardware Category' />
                  )}
                  clearOnEscape
                />
              </Stack>
              <TableContainer component={Paper}>
                <Table
                  sx={{ minWidth: 900 }}
                  aria-label='manage tag lists table'>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sortDirection={
                          orderBy === "Name" ? orderDirection : false
                        }>
                        <TableSortLabel
                          active={orderBy === "Name"}
                          direction={
                            orderBy === "Name" ? orderDirection : "asc"
                          }
                          onClick={() => handleSort("Name")}>
                          Tag List
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sortDirection={
                          orderBy === "Description" ? orderDirection : false
                        }>
                        <TableSortLabel
                          active={orderBy === "Description"}
                          direction={
                            orderBy === "Description" ? orderDirection : "asc"
                          }
                          onClick={() => handleSort("Description")}>
                          Description
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sortDirection={
                          orderBy === "HardwareCategory"
                            ? orderDirection
                            : false
                        }>
                        <TableSortLabel
                          active={orderBy === "HardwareCategory"}
                          direction={
                            orderBy === "HardwareCategory"
                              ? orderDirection
                              : "asc"
                          }
                          onClick={() => handleSort("HardwareCategory")}>
                          Hardware Category
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align='right'>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedTagLists.map((row) => (
                      <TableRow
                        key={row.TCTagListId}
                        hover
                        selected={selectedTagListIds.has(row.TCTagListId)}
                        onClick={() => toggleTagListSelection(row.TCTagListId)}
                        sx={{
                          cursor: "pointer",
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}>
                        <TableCell component='th' scope='row'>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}>
                            <Checkbox
                              checked={selectedTagListIds.has(row.TCTagListId)}
                            />
                            <span>{getTagListNameWithTenant(row)}</span>
                          </Box>
                        </TableCell>
                        <TableCell>{row.Description || ""}</TableCell>
                        <TableCell>{row.HardwareCategory || ""}</TableCell>
                        <TableCell
                          align='right'
                          onClick={(e) => e.stopPropagation()}>
                          <Tooltip title='Edit'>
                            <span>
                              <IconButton
                                color='primary'
                                onClick={() => handleOpenEditDialog(row)}
                                aria-label='edit tag list'
                                disabled={row.IsTagStandard ?? false}>
                                <EditIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title='Duplicate'>
                            <span>
                              <IconButton
                                color='primary'
                                onClick={() => handleOpenDuplicateDialog(row)}
                                aria-label='duplicate tag list'
                                disabled={row.IsTagStandard ?? false}>
                                <DuplicateIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title='History Log'>
                            <span>
                              <IconButton
                                color='primary'
                                aria-label='view tag list history'
                                onClick={() =>
                                  setHistoryTagList({
                                    id: row.TCTagListId,
                                    name: row.Name,
                                  })
                                }>
                                <HistoryIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title='Delete'>
                            <span>
                              <IconButton
                                color='error'
                                onClick={() => {
                                  setActiveTagList(row);
                                  setDeleteDialogOpen(true);
                                }}
                                aria-label='delete tag list'
                                disabled={row.IsTagStandard ?? false}>
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {paginatedTagLists.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align='center'>
                          No tag lists found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component='div'
                  count={filteredTagLists.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableContainer>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant='contained'
                  onClick={handleLoadIntoGrid}
                  disabled={selectedTagListIds.size === 0}>
                  Load into Grid
                </Button>
              </Box>
            </>
          ) : (
            <Box>
              <Autocomplete
                sx={{ minWidth: 300, mb: 2 }}
                options={removeTagListOptions}
                getOptionLabel={(option) => getTagListNameWithTenant(option)}
                value={
                  removeTagListOptions.find(
                    (tl) => tl.TCTagListId === selectedRemoveTagListId
                  ) || null
                }
                onChange={(_, newValue) => {
                  setSelectedRemoveTagListId(
                    newValue ? newValue.TCTagListId : -1
                  );
                  setSelectedTagToRemove(null);
                }}
                renderInput={(params) => (
                  <TextField {...params} label='Tag List' />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.TCTagListId === value.TCTagListId
                }
              />
              <Autocomplete
                sx={{ minWidth: 300, mb: 2 }}
                options={removableTags}
                getOptionLabel={(option) =>
                  option.TagName || `Tag ${option.TCTagLayerId}`
                }
                value={selectedTagToRemove}
                onChange={(_, newValue) => setSelectedTagToRemove(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label='Tag to Remove' />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.TCTagLayerId === value.TCTagLayerId
                }
                disabled={selectedRemoveTagListId === -1}
              />
              <Button
                variant='contained'
                color='error'
                onClick={handleRemoveTagFromList}
                disabled={
                  selectedRemoveTagListId === -1 || !selectedTagToRemove
                }>
                Remove Tag
              </Button>
            </Box>
          )}
        </DialogContent>

        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          fullWidth
          maxWidth='sm'>
          <DialogTitle>Edit Tag List</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label='Name'
              value={editForm.Name}
              onChange={(event) =>
                setEditForm((previous) => ({
                  ...previous,
                  Name: event.target.value,
                }))
              }
              sx={{ mt: 1, mb: 2 }}
            />
            <TextField
              fullWidth
              label='Description'
              value={editForm.Description}
              onChange={(event) =>
                setEditForm((previous) => ({
                  ...previous,
                  Description: event.target.value,
                }))
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label='Hardware Category'
              value={editForm.HardwareCategory}
              onChange={(event) =>
                setEditForm((previous) => ({
                  ...previous,
                  HardwareCategory: event.target.value,
                }))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isSubmitting || !editForm.Name.trim()}
              variant='contained'>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={duplicateDialogOpen}
          onClose={() => setDuplicateDialogOpen(false)}
          fullWidth
          maxWidth='sm'>
          <DialogTitle>Duplicate Tag List</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Provide a new name for the duplicated tag list.
            </DialogContentText>
            <TextField
              fullWidth
              label='New Tag List Name'
              value={duplicateName}
              onChange={(event) => setDuplicateName(event.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDuplicateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDuplicate}
              disabled={isSubmitting || !duplicateName.trim()}
              variant='contained'>
              Duplicate
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          fullWidth
          maxWidth='sm'>
          <DialogTitle>Delete Tag List</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this tag list and all of its tags?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDelete}
              color='error'
              disabled={isSubmitting}
              variant='contained'>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </DragModal>

      <TagListHistoryModal
        open={historyTagList !== null}
        onClose={() => setHistoryTagList(null)}
        tagListId={historyTagList?.id}
        tagListName={historyTagList?.name}
      />
    </>
  );
};
